import { useEffect, useState } from 'react';

import { EffectType, EquipmentType, ItemType } from '@game/enums';
import type { ModuleSlots, PopulatedItem } from '@game/game-data';
import type { ClientModuleItem } from '@game/shared-types';
import { Button, ExamineHover, Skeleton } from '@game/ui';
import pluralize from 'pluralize';
import type { JSX, ReactNode } from 'react';

import { equipmentTypeStatRequirement, qualityColor, qualityColorRGB } from '@constants/maps';
import { commaSeparatedNumber } from '@util/commaSeparatedNumber';
import { getItemSalePrice } from '@util/getItemSalePrice';
import { getModules, getModuleSlots } from '@util/getModuleSlots';
import { store } from '@store/store';

import { AttributesDescription } from './AttributeDescription';
import { SkillsDescription } from './SkillsDescription';

import './ExamineItem.less';

interface ExaminableItem {
  item: PopulatedItem;
  id: string;
  quantity?: number;
  quality: number;
  moduleSlots: ModuleSlots;
  modules: ClientModuleItem[];
}

interface ExamineItemProps {
  itemA: ExaminableItem;
  examinable?: boolean;
  itemB?: ExaminableItem;
  showTip?: boolean;
  tip?: ReactNode;
}

const ModulesSlotIcon = ({ module, examinable }: { module: ClientModuleItem; examinable: boolean }): ReactNode | undefined => {
  if (examinable) {
    return (
      <ExamineHover
        content={
          <ExamineItem
            itemA={{
              item: module.item,
              id: '',
              quality: module.quality,
              moduleSlots: getModuleSlots(module.item),
              modules: getModules(module.item),
            }}
          />
        }
      >
        <Button type="unstyled" className="examine-item--module">
          <Skeleton
            className="examine-item--module-image"
            src={module.item.imageUrl}
            width={32}
            height={32}
            alt={module.item.name}
            style={{
              border: `2px solid ${qualityColor[module.quality]}`,
              borderRadius: '4px',
              boxShadow: '2px 2px 2px 1px rgba(0, 0, 0, 0.2)',
            }}
          />
        </Button>
      </ExamineHover>
    );
  }

  return (
    <span className="examine-item--module">
      <Skeleton
        className="examine-item--module-image"
        src={module.item.imageUrl}
        width={32}
        height={32}
        alt={module.item.name}
        title={module.item.name}
        style={{
          border: `2px solid ${qualityColor[module.quality]}`,
          borderRadius: '4px',
          boxShadow: '2px 2px 2px 1px rgba(0, 0, 0, 0.2)',
        }}
      />
    </span>
  );
};

const EmptyModuleSlotIcon = (): JSX.Element => {
  return <span className="examine-item--empty-module-slot" />;
};

const ModuleSlotsDescription = ({
  modules,
  moduleSlots,
  examinable,
}: {
  modules: ClientModuleItem[];
  moduleSlots: ModuleSlots;
  examinable: boolean;
}): ReactNode | undefined => {
  if (!moduleSlots) {
    return;
  }

  if (typeof moduleSlots === 'number') {
    const showModuleSlots = modules.length > 0 || moduleSlots > 0;

    if (!showModuleSlots) {
      return null;
    }

    const freeModuleSlots = moduleSlots - modules.length;
    return (
      <div>
        Module slots ({modules.length}/{moduleSlots}){' '}
        <div className="examine-item--module-slots">
          {modules.map((module, index) => (
            <ModulesSlotIcon module={module} key={`${module.item.name}_${index}`} examinable={examinable} />
          ))}
          {Array.from({ length: freeModuleSlots }).map((_, index) => (
            <EmptyModuleSlotIcon key={index} />
          ))}
        </div>
      </div>
    );
  }

  if (moduleSlots.min === moduleSlots.max) {
    return `Module slots: ${moduleSlots.min}`;
  }

  return `Module slots: ${moduleSlots.min} to ${moduleSlots.max}`;
};

export const ExamineItem = ({ itemA, examinable = false }: ExamineItemProps): JSX.Element => {
  const { item, id, quality, modules, moduleSlots } = itemA;

  // If undefined default to 0
  const overdoseChance = item.type === ItemType.Consumable ? (item.overdoseChance ?? 0) : 0;
  const overdoseCooldown = item.type === ItemType.Consumable ? (item.overdoseCooldown ?? 0) : 0;
  const [overdoseCooldownTotal, setOverdoseCooldownTotal] = useState<number>(0);

  const cooldowns = store((s) => s.cooldowns);

  useEffect(() => {
    setOverdoseCooldownTotal(cooldowns.reduce((previous, current) => previous + current.overdoseChance, 0));
  }, [cooldowns]);

  return (
    <div
      className="examine-item"
      style={{
        border: `2px solid ${qualityColor[quality]}`,
        borderRadius: '0.5rem',
        boxShadow: `0px 0px 6px 0px rgba(${qualityColorRGB[quality]}, 0.5)`,

        background: 'black',
      }}
    >
      <div
        style={{
          boxShadow: `rgba(${qualityColorRGB[quality]}, 0.5) 0px 0px 6px 6px inset`,
          height: '100%',
          borderRadius: '6px',
        }}
      >
        <div
          style={{
            background: `linear-gradient(217deg, rgba(${qualityColorRGB[quality]}, 0.3), rgba(255, 0, 0, 0) 70.71%), linear-gradient(127deg, rgb(20 25 36), rgba(0, 255, 0, 0) 70.71%)`,
            padding: '0.5rem',
            height: '100%',
            borderRadius: '5px',
          }}
        >
          <div className="examine-item-top">
            <div className="examine-item-top-left">
              <div
                className="examine-item--name"
                style={{
                  color: qualityColor[quality],
                }}
              >
                {item.qualityAdjectives[quality]} {item.name}
              </div>
              <div className="examine-item--type">
                {EquipmentType[item.type === ItemType.Equipment ? item.equipmentType : -1] ?? ItemType[item.type]}
              </div>
            </div>
            <Skeleton
              className="examine-item--image"
              src={item.imageUrl}
              width={60}
              height={60}
              style={{
                borderRadius: '1rem',
              }}
              alt={item.name}
            />
          </div>
          <div className="examine-item-body">
            {overdoseChance > 0 ||
              (overdoseCooldown > 0 && (
                <div className="examine-item--overdose">
                  <div className="examine-item--overdose-cooldown">
                    Chance to overdose:{' '}
                    {overdoseCooldownTotal > 0 ? (
                      <>
                        {overdoseCooldownTotal + overdoseChance}%{' '}
                        <small>
                          ({overdoseChance}% from this item + {overdoseCooldownTotal}% from active cooldowns)
                        </small>
                      </>
                    ) : (
                      `${overdoseChance}%`
                    )}
                  </div>
                  {overdoseCooldown > 0 && (
                    <div className="examine-item--overdose-cooldown">Consumption cooldown: {pluralize('minute', overdoseCooldown, true)}</div>
                  )}
                </div>
              ))}
            {AttributesDescription({ item, id, quality })}
            {SkillsDescription({ item, quality })}
            {ModuleSlotsDescription({ modules, moduleSlots, examinable })}
            {item.level > 0 && item.type === ItemType.Equipment && item.equipmentType && (
              <div className="examine-item--requirements">
                Required {equipmentTypeStatRequirement(item.equipmentType)}: {item.level}
              </div>
            )}
          </div>
          <div className="examine-item--description">{item.qualityDescriptions[quality]}</div>
          <ExamineItemBottom item={item} quality={quality} />
        </div>
      </div>
    </div>
  );
};

function ExamineItemBottom({ item, quality }: { item: PopulatedItem; quality: number }): ReactNode {
  if (item.type === ItemType.Key) {
    return null;
  }

  if (item.type === ItemType.Consumable && item.consumeEffects?.some((consumeEffect) => consumeEffect.type === EffectType.AcceptMission)) {
    return null;
  }

  return (
    <div className="examine-item-bottom">
      <span className="examine-item--value">{commaSeparatedNumber(getItemSalePrice(item, quality, 1))} cr.</span>
    </div>
  );
}
